import duckify from '../../tools/duckify';

export const namespace = 'cassie';
export const store = 'shoreSAnalysis';

export const Types = duckify(namespace, store, [
  'RUN',
  'EXTRACT_REFERENCES',
  'SET_EXTRACTION',
  'SET_RESULT',
  'SET_LOADING',
  'SET_ERROR',
  'RESET_ANALYSIS',
  'RESET',
]);

export const Actions = {
  /**
   * @function extractReferences
   * @description Extracts the reference coastlines from the selected imagery
   * and caches them. Runs once when the processing page opens, it
   * needs no baseline and no transects, so it doesn't wait on the user.
   * @param {Array} selectedImages - Dates chosen in ShoreSPeriodImageChooser.
   * @param {string} key - Fingerprint of the extraction inputs; a run whose key
   *   still matches the cached one is skipped instead of redone.
   * **Called by:
   * @see {ProcessingShoreSPage}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type EXTRACT_REFERENCES.
   */
  extractReferences: (selectedImages, key) => ({
    type: Types.EXTRACT_REFERENCES,
    payload: { selectedImages, key },
  }),

  /**
   * @function setExtraction
   * @description Merges a partial update into the `extraction` sub-state.
   * @param {Object} extraction
   * **Called by:
   * @see {shoreSAnalysis/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_EXTRACTION.
   */
  setExtraction: (extraction) => ({
    type: Types.SET_EXTRACTION,
    payload: extraction,
  }),

  /**
   * @function run
   * @description Runs the transect-driven half of the Shore-S validation: cleans the cached
   * reference candidates with the configured transects, then intersects them
   * and the modeled coastlines against those transects and computes distances,
   * the time-proximity merge and the error metrics (turf/pure-JS) — mirrors
   * `performAnalysis` in `src/store/ducks/imagery/saga/shoreline.js`, including
   * how transects are (re)generated from spacing/extent rather than trusting
   * client-drawn geometry.
   *
   * Requires `extraction.status === 'READY'`. Imagery is never touched here,
   * the reference-coastline extraction runs earlier via `extractReferences`.
   * @param {Array<Array<number>>} baselineCoords - `[[lng,lat], ...]` (WGS84, from Google Maps).
   * @param {number} transectsStep - Spacing between transects, in meters.
   * @param {number} transectsExtent - Length of each transect, in meters.
   * @param {{toleranceInDays: (number|undefined), direction: (string|undefined)}} [mergeOptions]
   * **Called by:
   * @see {ShoreSAnalysisSteps}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type RUN.
   */
  run: (baselineCoords, transectsStep, transectsExtent, mergeOptions) => ({
    type: Types.RUN,
    payload: { baselineCoords, transectsStep, transectsExtent, mergeOptions },
  }),

  /**
   * @function setResult
   * @description Publishes the full result of the validation run (distances, merged rows,
   * error/aggregated/measured-deviation metrics) to the store.
   * @param {Object} result
   * **Called by:
   * @see {shoreSAnalysis/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_RESULT.
   */
  setResult: (result) => ({
    type: Types.SET_RESULT,
    payload: result,
  }),

  /**
   * @function setLoading
   * @description Toggles the Shore-S analysis slice's loading flag.
   * @param {boolean} loading
   * **Called by:
   * @see {shoreSAnalysis/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_LOADING.
   */
  setLoading: (loading) => ({
    type: Types.SET_LOADING,
    payload: { loading },
  }),

  /**
   * @function setError
   * @description Records an error from the validation run. Known values: null (cleared) | any
   * error message string.
   * @param {string|null} error
   * **Called by:
   * @see {shoreSAnalysis/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_ERROR.
   */
  setError: (error) => ({
    type: Types.SET_ERROR,
    payload: { error },
  }),

  /**
   * @function resetAnalysis
   * @description Clears the transect-driven analysis result while keeping the cached
   * reference extraction, so a re-run doesn't hit the imagery again.
   * **Called by:
   * @see {ShoreSAnalysisSteps}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type RESET_ANALYSIS.
   */
  resetAnalysis: () => ({
    type: Types.RESET_ANALYSIS,
  }),

  /**
   * @function reset
   * @description Clears the Shore-S analysis result (e.g. when leaving the flow).
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type RESET.
   */
  reset: () => ({
    type: Types.RESET,
  }),
};

export default Actions;
